// Leitura da planilha que a pessoa sobe e montagem do modelo .xlsx (out/26).
// As regras (campos, validação) moram em shared/importacao.ts; aqui só o que
// mexe com o arquivo. O servidor repete toda a validação antes de gravar.
import * as XLSX from "xlsx";
import {
  ABA_DADOS, ABA_EXISTENTES, ABA_INSTRUCOES, ABA_LISTAS, ABAS_NAO_LIDAS, LIMITE_LINHAS, NOME_TIPO,
  ROTULO_FORMATO, ROTULO_FUNIL, ROTULO_REDE, campoDoCabecalho, campoTitulo, camposDo, formatarDataBR,
  letraColuna, linhaTemConteudo, normalizar, valorParaPlanilha, type CampoDef, type EntradaLote, type TipoImportacao,
} from "@shared/importacao";
import { etapasDePauta, etapaInicial, rotuloEtapa, type EtapaDef } from "@shared/etapas";

const p2 = (n: number) => String(n).padStart(2, "0");
// No navegador (xlsx.mjs) SSF e CFB vêm como exportação nomeada; no node, a
// versão CommonJS só expõe os dois no objeto padrão.
const XL: any = (XLSX as any).CFB ? XLSX : ((XLSX as any).default ?? XLSX);

// ─── Ler ───────────────────────────────────────────────────────────────────

// linhas[i] é a linha i+1 do Excel (a leitura sempre começa na linha 1, mesmo
// quando a área usada começa mais abaixo). links: o que cada célula com
// hiperlink ou fórmula HYPERLINK/HIPERLINK traz ("linha:coluna", base 0).
// cortada: a aba tem mais linhas do que a leitura alcança (arquivo grande).
export type LinkCelula =
  | { tipo: "url"; url: string }          // hiperlink, ou HYPERLINK("http...") sem mais nada na fórmula
  | { tipo: "calculado" }                 // fórmula com HYPERLINK que monta ou condiciona o endereço
  | { tipo: "sem_http"; url: string };    // HYPERLINK("www...") sem http/https/mailto
export interface AbaLida { nome: string; linhas: string[][]; links: Record<string, LinkCelula>; cortada?: { linhas: number } }

// CSV do Excel brasileiro costuma vir em windows-1252: se o UTF-8 não fecha, cai para ele.
export function decodificarTexto(u8: Uint8Array): string {
  let texto: string;
  try { texto = new TextDecoder("utf-8", { fatal: true }).decode(u8); }
  catch { texto = decodificar1252(u8); }
  return texto.replace(/^\uFEFF/, "");
}

// windows-1252 feito à mão: alguns ambientes tratam o rótulo como latin1 e
// trocam € “ ” – … por caracteres de controle. Fora de 0x80 a 0x9F é igual ao latin1.
const CP1252_80_9F = "\u20AC\uFFFD\u201A\u0192\u201E\u2026\u2020\u2021\u02C6\u2030\u0160\u2039\u0152\uFFFD\u017D\uFFFD" +
  "\uFFFD\u2018\u2019\u201C\u201D\u2022\u2013\u2014\u02DC\u2122\u0161\u203A\u0153\uFFFD\u017E\u0178";
function decodificar1252(u8: Uint8Array): string {
  let out = "";
  for (let i = 0; i < u8.length; i += 8192) {
    const parte = u8.subarray(i, i + 8192);
    out += String.fromCharCode(...Array.from(parte, b => b >= 0x80 && b <= 0x9f ? CP1252_80_9F.charCodeAt(b - 0x80) : b));
  }
  return out;
}

// Bem acima do limite de linhas: quem passa dele recebe a mensagem do limite.
export const LINHAS_LIDAS = LIMITE_LINHAS * 4 + 20;

// Até este tamanho o arquivo é lido inteiro (sem sheetRows): o limite de 500
// linhas já dá a contagem certa. Acima, a leitura para em LINHAS_LIDAS; a aba
// que volta com !fullref maior que o lido é cortada (erro claro). Sem !fullref
// não dá para saber (falta quando o arquivo não tem <dimension> ou traz
// <dimension ref="A1"/>, e o corte pode cair num buraco de linhas vazias): essa
// aba é lida de novo, inteira.
export const TAMANHO_LEITURA_INTEIRA = 5 * 1024 * 1024;

// Planilha de verdade: .xlsx é ZIP (PK), .xls antigo é OLE (D0 CF 11 E0). O resto
// (HTML ou TSV salvo como .xls) é texto.
function ehZipOuOle(u8: Uint8Array): boolean {
  return (u8[0] === 0x50 && u8[1] === 0x4b) || (u8[0] === 0xd0 && u8[1] === 0xcf && u8[2] === 0x11 && u8[3] === 0xe0);
}

export function lerArquivo(dados: ArrayBuffer | Uint8Array, nomeArquivo: string, opcoes: { tamanhoLeituraInteira?: number } = {}): AbaLida[] {
  const u8 = dados instanceof Uint8Array ? dados : new Uint8Array(dados);
  const ext = nomeArquivo.toLowerCase().split(".").pop() ?? "";
  const limiteInteira = opcoes.tamanhoLeituraInteira ?? TAMANHO_LEITURA_INTEIRA;
  let wb: XLSX.WorkBook;
  let comTeto = false;
  if (ext === "csv" || ext === "tsv" || ext === "txt" || !ehZipOuOle(u8)) {
    // Texto (CSV, TSV, HTML com outra extensão): lido como texto puro e inteiro.
    // Sem raw, "05/11/2026" viraria data americana.
    wb = XLSX.read(decodificarTexto(u8), { type: "string", raw: true });
  } else if (u8.length <= limiteInteira) {
    wb = XLSX.read(u8, { type: "array", cellNF: true });
  } else {
    // Arquivo grande: sheetRows evita travar numa aba formatada até o fim.
    comTeto = true;
    wb = XLSX.read(u8, { type: "array", cellNF: true, sheetRows: LINHAS_LIDAS });
  }
  const p = (wb.Workbook as any)?.WBProps?.date1904;
  const date1904 = p === true || p === 1 || p === "1" || p === "true";
  const cortadas = new Map<string, number>();
  if (comTeto) {
    const semTamanho: string[] = [];
    for (const nome of wb.SheetNames) {
      if (ABAS_NAO_LIDAS.some(n => normalizar(n) === normalizar(nome))) continue;
      const ws = wb.Sheets[nome];
      const cheio = ws?.["!fullref"] ? XLSX.utils.decode_range(String(ws["!fullref"])) : null;
      const lido = ws?.["!ref"] ? XLSX.utils.decode_range(String(ws["!ref"])) : null;
      if (cheio && (!lido || cheio.e.r > lido.e.r)) cortadas.set(nome, cheio.e.r + 1);
      else if (!cheio) semTamanho.push(nome);
    }
    if (semTamanho.length) {
      const inteiro = XLSX.read(u8, { type: "array", cellNF: true, sheets: semTamanho });
      for (const nome of semTamanho) if (inteiro.Sheets[nome]) wb.Sheets[nome] = inteiro.Sheets[nome];
    }
  }
  return wb.SheetNames.map(nome => {
    const aba: AbaLida = { nome, ...lerAba(wb.Sheets[nome], date1904) };
    if (cortadas.has(nome)) aba.cortada = { linhas: cortadas.get(nome)! };
    return aba;
  });
}

// Aba maior do que a leitura alcança: erro claro em vez de importar só o começo.
export function limitesDaAba(aba: AbaLida | null): string[] {
  if (!aba?.cortada) return [];
  const f = (n: number) => n.toLocaleString("pt-BR");
  return [`A aba "${aba.nome}" tem ${f(aba.cortada.linhas)} linhas; o limite de leitura é ${f(LINHAS_LIDAS)}. Divida o arquivo (ou apague as linhas vazias com formatação no fim da aba).`];
}

// Só as abas que a importação pode ler (nunca Instruções, Listas ou Já no sistema).
export function abasLegiveis(abas: AbaLida[]): AbaLida[] {
  return abas.filter(a =>
    !ABAS_NAO_LIDAS.some(n => normalizar(n) === normalizar(a.nome)) &&
    a.linhas.some(l => l.some(v => String(v ?? "").trim())));
}

const ERRO_POR_CODIGO: Record<number, string> = {
  0x00: "#NULL!", 0x07: "#DIV/0!", 0x0F: "#VALUE!", 0x17: "#REF!", 0x1D: "#NAME?", 0x24: "#NUM!", 0x2A: "#N/A", 0x2B: "#GETTING_DATA",
};
// Formato com hora: tira texto entre aspas e prefixos como [$-416] e [Red].
function formatoTemHora(z: string): boolean {
  return /[hs]/i.test(z.replace(/"[^"]*"/g, "").replace(/\[(\$[^\]]*|[a-z]+\d*)\]/gi, ""));
}

export function textoDaCelula(cell: XLSX.CellObject | undefined, date1904 = false): string {
  if (!cell) return "";
  // Erro do Excel (#N/D, #VALOR!...) não vira vazio: vai como texto e a linha acusa erro.
  if (cell.t === "e") return String(cell.w ?? ERRO_POR_CODIGO[Number(cell.v)] ?? "#ERRO");
  if (cell.v == null) return "";
  if (cell.t === "b") return cell.v ? "VERDADEIRO" : "FALSO";
  if (cell.t === "d" && cell.v instanceof Date) {
    const d = cell.v;
    return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()}`;
  }
  if (cell.t === "n") {
    const z = typeof cell.z === "string" ? cell.z : "";
    if (z && XL.SSF.is_date(z)) {
      // Data do Excel (número de série) vira DD/MM/AAAA sem passar por fuso,
      // assim sai igual no campo de data e em Observações.
      let v = Number(cell.v);
      if (Math.abs(v - Math.round(v)) < 1e-6) v = Math.round(v);
      const d = XL.SSF.parse_date_code(v, { date1904 });
      if (!d) return String(cell.v);
      if (v < 1) return `${p2(d.H)}:${p2(d.M)}`;   // só hora (nos dois sistemas de data)
      const data = `${p2(d.d)}/${p2(d.m)}/${d.y}`;
      return formatoTemHora(z) && (d.H || d.M) ? `${data} ${p2(d.H)}:${p2(d.M)}` : data;
    }
    // Número com formato próprio (código 007, 12%) sai como aparece na célula.
    if (z && z !== "General" && cell.w != null) return String(cell.w);
    return String(cell.v);
  }
  return String(cell.v);
}

// O Target do hiperlink vem com o escape do XML (&amp;); a fórmula HYPERLINK traz o
// endereço entre aspas, com "" para aspas.
export function desfazerEscapeXml(s: string): string {
  return s
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}
const EH_URL = /^(https?:\/\/|mailto:)/i;
// Fórmula de link. O endereço só vale quando a fórmula inteira é exatamente
// HYPERLINK("literal") ou HYPERLINK("literal"; nome), sem nada antes nem
// depois. HYPERLINK em qualquer outro ponto (dentro de SE/SEERRO, =+HYPERLINK,
// HYPERLINK(...)&C2, endereço concatenado) é link calculado: vai o texto da
// célula, com aviso. Nunca grava pedaço de endereço.
const TEM_HYPERLINK = /(?:^|[^A-Za-z0-9_.])(?:_xlfn\.)?(?:HYPERLINK|HIPERLINK)\s*\(/i;
const INICIO_HYPERLINK = /^\s*=?\s*(?:_xlfn\.)?(?:HYPERLINK|HIPERLINK)\s*\(/i;
// f[j] é a aspa que abre; devolve o índice depois da aspa que fecha ("" é aspa no texto), ou -1.
function fimDoTexto(f: string, j: number): number {
  for (let k = j + 1; k < f.length; k++) {
    if (f[k] !== '"') continue;
    if (f[k + 1] === '"') { k++; continue; }
    return k + 1;
  }
  return -1;
}
export function linkDaFormula(f: string): LinkCelula | null {
  if (!f || !TEM_HYPERLINK.test(f)) return null;
  const m = INICIO_HYPERLINK.exec(f);
  if (!m) return { tipo: "calculado" };
  let j = m[0].length;
  while (/\s/.test(f[j] ?? "")) j++;
  if (f[j] !== '"') return { tipo: "calculado" };
  const fimLit = fimDoTexto(f, j);
  if (fimLit < 0) return { tipo: "calculado" };
  const url = f.slice(j + 1, fimLit - 1).replace(/""/g, '"').trim();
  j = fimLit;
  while (/\s/.test(f[j] ?? "")) j++;
  if (f[j] === ",") j++;
  else if (f[j] === ";") j++;
  else if (f[j] !== ")") return { tipo: "calculado" };
  // Acha o parêntese que fecha a chamada (pulando textos e parênteses do 2º argumento).
  let prof = 0;
  for (; j < f.length; j++) {
    const ch = f[j];
    if (ch === '"') { const k = fimDoTexto(f, j); if (k < 0) return { tipo: "calculado" }; j = k - 1; continue; }
    if (ch === "(") prof++;
    else if (ch === ")") { if (prof === 0) break; prof--; }
  }
  if (j >= f.length) return { tipo: "calculado" };
  if (f.slice(j + 1).trim() !== "") return { tipo: "calculado" };
  if (!EH_URL.test(url)) return { tipo: "sem_http", url };
  return { tipo: "url", url };
}

export function linkDaCelula(cell: XLSX.CellObject | undefined): LinkCelula | null {
  if (!cell) return null;
  const alvo = desfazerEscapeXml(String((cell as any).l?.Target ?? "").trim());
  if (EH_URL.test(alvo)) return { tipo: "url", url: alvo };
  const f = typeof (cell as any).f === "string" ? (cell as any).f as string : "";
  return linkDaFormula(f);
}
// Endereço que vai ser gravado, quando há um (atalho para quem só quer a URL).
export function enderecoDaCelula(cell: XLSX.CellObject | undefined): string | null {
  const l = linkDaCelula(cell);
  return l?.tipo === "url" ? l.url : null;
}

// Endereço no texto de um aviso: até 200 caracteres.
export function cortarEndereco(u: string): string {
  return u.length > 200 ? `${u.slice(0, 200)}…` : u;
}

function lerAba(ws: XLSX.WorkSheet | undefined, date1904: boolean): { linhas: string[][]; links: Record<string, LinkCelula> } {
  const links: Record<string, LinkCelula> = {};
  if (!ws || !ws["!ref"]) return { linhas: [], links };
  const r = XLSX.utils.decode_range(ws["!ref"]);
  const out: string[][] = [];
  for (let R = 0; R <= r.e.r; R++) {
    const linha: string[] = [];
    for (let C = 0; C <= r.e.c; C++) {
      const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })] as XLSX.CellObject | undefined;
      linha.push(textoDaCelula(cell, date1904));
      const link = linkDaCelula(cell);
      if (link) links[`${R}:${C}`] = link;
    }
    out.push(linha);
  }
  // Célula mesclada: o valor da âncora desce pelas linhas da mescla, só na coluna dela.
  for (const m of (ws["!merges"] ?? []) as XLSX.Range[]) {
    const v = out[m.s.r]?.[m.s.c] ?? "";
    const l = links[`${m.s.r}:${m.s.c}`];
    for (let R = m.s.r + 1; R <= m.e.r && R < out.length; R++) {
      if (!String(out[R][m.s.c] ?? "").trim()) out[R][m.s.c] = v;
      if (l && !links[`${R}:${m.s.c}`]) links[`${R}:${m.s.c}`] = l;
    }
  }
  return { linhas: out, links };
}

// Linha do cabeçalho: a primeira (até a 15ª) com o título e mais um campo conhecido.
export function acharCabecalho(linhas: string[][], tipo: TipoImportacao): number {
  const titulo = campoTitulo(tipo).key;
  let melhor = -1, melhorQtd = 0;
  for (let i = 0; i < Math.min(15, linhas.length); i++) {
    const campos = linhas[i].map(h => campoDoCabecalho(tipo, h)?.key).filter(Boolean) as string[];
    if (campos.includes(titulo) && campos.length >= 2) return i;
    if (campos.length > melhorQtd) { melhor = i; melhorQtd = campos.length; }
  }
  return melhor >= 0 ? melhor : 0;
}

// Aba de dados: a do modelo ("Sugestões de tema" ou "Cards"); senão a primeira
// que tem a coluna de título. Devolve também o tipo que o arquivo sugere.
export function escolherAba(abas: AbaLida[], tipo: TipoImportacao): { aba: AbaLida | null; tipoSugerido: TipoImportacao | null } {
  const lidas = abasLegiveis(abas);
  for (const t of ["temas", "pautas"] as TipoImportacao[]) {
    const a = lidas.find(x => normalizar(x.nome) === normalizar(ABA_DADOS[t]));
    if (a) return { aba: a, tipoSugerido: t };
  }
  const comTitulo = lidas.find(a => {
    const i = acharCabecalho(a.linhas, tipo);
    return (a.linhas[i] ?? []).some(h => campoDoCabecalho(tipo, h)?.key === campoTitulo(tipo).key);
  });
  return { aba: comTitulo ?? lidas[0] ?? null, tipoSugerido: null };
}

export function montarEntrada(aba: AbaLida, tipo: TipoImportacao, ignorar: number[] = []): EntradaLote {
  const ic = acharCabecalho(aba.linhas, tipo);
  const cab = aba.linhas[ic] ?? [];
  // Corta colunas vazias à direita (sem cabeçalho e sem valor).
  let largura = cab.length;
  const temValor = (c: number) => aba.linhas.slice(ic).some(l => String(l[c] ?? "").trim());
  while (largura > 0 && !temValor(largura - 1)) largura--;
  // Colunas de link: vale o endereço do hiperlink (ou da fórmula HYPERLINK).
  // Se o texto da célula já é um endereço e o hiperlink aponta para outro, vale
  // o texto, com aviso na prévia.
  const colLink = new Set(cab.map((h, i) => campoDoCabecalho(tipo, h)?.link ? i : -1).filter(i => i >= 0));
  const semBarra = (u: string) => u.trim().replace(/\/+$/, "");
  const linhas = aba.linhas.slice(ic + 1)
    .map((c, i) => {
      const R = ic + 1 + i;   // índice na aba = linha R+1 do Excel
      const avisos: string[] = [];
      const valores = c.slice(0, largura).map((v, C) => {
        const texto = String(v ?? "");
        const link = colLink.has(C) ? aba.links?.[`${R}:${C}`] : undefined;
        if (!link) return texto;
        const coluna = String(cab[C] ?? "").trim();
        const oTexto = texto.trim() ? ` (${cortarEndereco(texto.trim())})` : " (vazio)";
        if (link.tipo === "calculado") {
          avisos.push(`"${coluna}": link calculado por fórmula, confira. Vai o texto da célula${oTexto}.`);
          return texto;
        }
        if (link.tipo === "sem_http") {
          avisos.push(`"${coluna}": endereço sem http na fórmula (${cortarEndereco(link.url)}), confira. Vai o texto da célula${oTexto}.`);
          return texto;
        }
        if (EH_URL.test(texto.trim()) && semBarra(texto) !== semBarra(link.url)) {
          avisos.push(`"${coluna}": o texto da célula é um endereço e o hiperlink aponta para outro (${cortarEndereco(link.url)}). Vale o texto.`);
          return texto;
        }
        return link.url;
      });
      return { n: R + 1, c: valores, ...(avisos.length ? { avisos } : {}) };
    })
    .filter(l => linhaTemConteudo(l.c, ignorar));
  return { tipo, cabecalhos: cab.slice(0, largura).map(h => String(h ?? "")), linhas, ignorar };
}

// ─── Modelo ────────────────────────────────────────────────────────────────

export interface OpcoesModelo {
  tipo: TipoImportacao;
  campos: string[];              // keys escolhidas (o título entra sempre)
  clienteNome: string;
  quadro: EtapaDef[];
  sugestoes: { canais: string[]; formatos: string[]; linhas: string[]; territorios: string[] };
  existentes?: { mes: string; itens: Record<string, unknown>[] } | null;
  geradoPor?: string;
  geradoEm?: Date;
}

export function nomeArquivoModelo(o: { tipo: TipoImportacao; clienteNome: string; geradoEm?: Date }): string {
  const d = o.geradoEm ?? new Date();
  const cli = normalizar(o.clienteNome).replace(/ /g, "-") || "cliente";
  const tipo = o.tipo === "temas" ? "sugestoes-de-tema" : "cards";
  return `modelo-${tipo}-${cli}-${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}.xlsx`;
}

const MES_NOME = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
export function nomeMes(mes: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(mes);
  return m ? `${MES_NOME[Number(m[2]) - 1]} de ${m[1]}` : mes;
}

function limiteTexto(c: CampoDef): string {
  if (c.max) return `até ${c.max} caracteres`;
  if (c.textoLongo) return "texto longo";
  return "";
}

export function camposDoModelo(tipo: TipoImportacao, escolhidos: string[]): CampoDef[] {
  const set = new Set(escolhidos);
  return camposDo(tipo).filter(c => c.obrigatorio || set.has(c.key));
}

export function gerarModelo(o: OpcoesModelo): Uint8Array {
  const campos = camposDoModelo(o.tipo, o.campos);
  const etapas = etapasDePauta(o.quadro);
  const ini = rotuloEtapa(etapaInicial(o.quadro), o.quadro);
  const quando = o.geradoEm ?? new Date();
  const tem = (k: string) => campos.some(c => c.key === k);
  const item = o.tipo === "temas" ? "sugestão de tema" : "card";
  const abaDados = ABA_DADOS[o.tipo];

  // Instruções
  const I: (string | number)[][] = [];
  I.push([`Modelo de importação: ${NOME_TIPO[o.tipo]} · ${o.clienteNome}`]);
  I.push([`Gerado em ${formatarDataBR(`${quando.getFullYear()}-${p2(quando.getMonth() + 1)}-${p2(quando.getDate())}`)}${o.geradoPor ? ` por ${o.geradoPor}` : ""}.`]);
  I.push([]);
  I.push(["Como usar"]);
  I.push([`1. Preencha a aba "${abaDados}", uma linha por ${item}. A primeira linha é o cabeçalho: não mude o nome das colunas.`]);
  I.push(["2. A linha 2 é um exemplo. Apague antes de subir; se ficar, o sistema ignora."]);
  I.push(["3. No Social Hub, em Pautas, clique em Importar / exportar, escolha Importar planilha e suba este arquivo."]);
  I.push(["4. Confira a prévia. Nada é gravado antes de você confirmar, e as linhas com erro ficam de fora com o motivo."]);
  I.push([]);
  I.push(["Regras deste cliente"]);
  if (o.tipo === "temas") {
    I.push(["Toda sugestão entra na coluna Sugestão de Tema. Para virar card, alguém aprova no quadro."]);
  } else if (tem("coluna")) {
    I.push([`Etapa: use uma etapa do quadro deste cliente: ${etapas.map(e => e.label).join(", ")}. Vazio: ${ini}.`]);
  } else {
    I.push([`Todos os cards entram na etapa ${ini}.`]);
  }
  I.push(["Datas: DD/MM/AAAA, por exemplo 05/11/2026, ou célula de data do Excel."]);
  if (tem("codigo")) {
    I.push(["Código: único. Não pode repetir na planilha nem já existir no Social Hub, nem em outro cliente. Até 20 caracteres, por exemplo NV01."]);
  }
  if (tem("parentCodigo")) {
    I.push([`Peça relacionada: para ligar um comentário de porta-voz ou um story a outra peça, ponha em "Código da peça-mãe" o código da peça principal. A mãe pode estar nesta planilha ou já existir no Social Hub${o.tipo === "pautas" ? " como card" : ""}.`]);
    I.push(["Exemplo: o post NV07 tem um comentário de porta-voz. O comentário leva o código NV07-C e, em Código da peça-mãe, NV07."]);
    if (tem("portaVoz") || tem("anguloComentario")) {
      I.push([o.tipo === "temas"
        ? 'Comentário de porta-voz: preencha Porta-voz e Ângulo do comentário e use um formato com a palavra "Comentário".'
        : "Comentário de porta-voz: preencha Porta-voz e Ângulo do comentário e use o formato Comentário."]);
    }
  }
  if (tem("funil")) I.push(["Funil: T, M ou F (Topo, Meio ou Fundo)."]);
  if (tem("canais")) I.push(["Canais: mais de um, separe por vírgula."]);
  if (o.tipo === "pautas" && (tem("rede") || tem("formato"))) I.push(["Rede vazia entra como Instagram e formato vazio como Feed, como no card novo."]);
  I.push(["Célula vazia é campo vazio."]);
  I.push(['Colunas a mais: qualquer coluna que não seja campo do Social Hub vai para Observações, no formato "Nome da coluna: valor". Na prévia dá para mandar ignorar a coluna.']);
  I.push([`Até ${LIMITE_LINHAS} linhas por arquivo.`]);
  if (o.existentes) {
    I.push([`A aba "${ABA_EXISTENTES}" mostra o que já existe em ${nomeMes(o.existentes.mes)}, no mesmo formato, só para consulta. A importação não lê essa aba.`]);
  }
  I.push([]);
  I.push(["Campos deste modelo"]);
  I.push(["Coluna", "Obrigatório", "O que pôr", "Limite"]);
  for (const c of campos) {
    let ajuda = c.ajuda;
    if (c.tipo === "etapa") ajuda = `${c.ajuda} Etapas deste cliente: ${etapas.map(e => e.label).join(", ")}. Vazio: ${ini}.`;
    I.push([c.label, c.obrigatorio ? "Sim" : "Não", ajuda, limiteTexto(c)]);
  }
  const wsI = XLSX.utils.aoa_to_sheet(I);
  wsI["!cols"] = [{ wch: 26 }, { wch: 12 }, { wch: 100 }, { wch: 20 }];

  // Listas (para as listas suspensas)
  type Lista = { nome: string; valores: string[]; estrita: boolean; erro: string };
  const listas: Record<string, Lista> = {};
  const unicos = (xs: string[]) => [...new Set(xs.map(s => String(s ?? "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR"));
  for (const c of campos) {
    if (c.tipo === "etapa") listas[c.key] = { nome: "Etapa", valores: etapas.map(e => e.label), estrita: true, erro: "Escolha uma etapa da lista. São as etapas do quadro deste cliente." };
    if (c.tipo === "rede") listas[c.key] = { nome: "Rede", valores: Object.values(ROTULO_REDE), estrita: true, erro: "Escolha uma rede da lista." };
    if (c.tipo === "formato") listas[c.key] = { nome: "Formato", valores: Object.values(ROTULO_FORMATO), estrita: true, erro: "Escolha um formato da lista." };
    if (c.tipo === "funil") listas[c.key] = { nome: "Funil", valores: Object.values(ROTULO_FUNIL), estrita: true, erro: "Use T, M ou F." };
    if (c.tipo === "canais") {
      const v = unicos(o.sugestoes.canais);
      listas[c.key] = { nome: "Canais (sugestões)", valores: v.length ? v : ["Instagram", "LinkedIn", "TikTok", "YouTube"], estrita: false, erro: "" };
    }
    if (o.tipo === "temas" && c.key === "formato") {
      const v = unicos(o.sugestoes.formatos);
      if (v.length) listas[c.key] = { nome: "Formato (sugestões)", valores: v, estrita: false, erro: "" };
    }
    if (c.key === "linhaEditorial") { const v = unicos(o.sugestoes.linhas); if (v.length) listas[c.key] = { nome: "Linha editorial (sugestões)", valores: v, estrita: false, erro: "" }; }
    if (c.key === "territorio") { const v = unicos(o.sugestoes.territorios); if (v.length) listas[c.key] = { nome: "Território (sugestões)", valores: v, estrita: false, erro: "" }; }
  }
  const chavesLista = campos.map(c => c.key).filter(k => listas[k]);
  const maxLista = Math.max(0, ...chavesLista.map(k => listas[k].valores.length));
  const L: string[][] = [chavesLista.map(k => listas[k].nome)];
  for (let i = 0; i < maxLista; i++) L.push(chavesLista.map(k => listas[k].valores[i] ?? ""));
  const wsL = XLSX.utils.aoa_to_sheet(L.length > 1 || chavesLista.length ? L : [["Sem listas neste modelo"]]);
  wsL["!cols"] = chavesLista.map(() => ({ wch: 28 }));

  // Dados: cabeçalho + 1 linha de exemplo
  const cab = campos.map(c => c.label);
  const exemplo = campos.map(c => {
    if (c.tipo === "etapa") return ini;
    return c.exemplo;
  });
  const wsD = XLSX.utils.aoa_to_sheet([cab, exemplo]);
  const largura = (c: CampoDef) => c.tipo === "titulo" ? 50 : c.textoLongo ? 40 : c.tipo === "data" ? 14 : 18;
  wsD["!cols"] = campos.map(c => ({ wch: largura(c) }));
  wsD["!autofilter"] = { ref: `A1:${letraColuna(campos.length - 1)}1` };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, wsI, ABA_INSTRUCOES);
  XLSX.utils.book_append_sheet(wb, wsD, abaDados);
  if (o.existentes) {
    const linhas = o.existentes.itens.map(it => campos.map(c => valorParaPlanilha(c, it[c.key], o.quadro)));
    const wsE = XLSX.utils.aoa_to_sheet([cab, ...linhas]);
    wsE["!cols"] = wsD["!cols"];
    if (linhas.length) wsE["!autofilter"] = { ref: `A1:${letraColuna(campos.length - 1)}${linhas.length + 1}` };
    XLSX.utils.book_append_sheet(wb, wsE, ABA_EXISTENTES);
  }
  XLSX.utils.book_append_sheet(wb, wsL, ABA_LISTAS);

  // Listas suspensas: a versão livre do xlsx não grava validação de dados, então
  // ela entra direto no XML da aba de dados.
  const validacoes = campos.map((c, i) => {
    const l = listas[c.key];
    if (!l || !l.valores.length) return "";
    const colLista = letraColuna(chavesLista.indexOf(c.key));
    const ref = `${letraColuna(i)}2:${letraColuna(i)}${LIMITE_LINHAS + 50}`;
    const formula = `'${ABA_LISTAS}'!$${colLista}$2:$${colLista}$${l.valores.length + 1}`;
    const erro = l.estrita
      ? ` showErrorMessage="1" errorStyle="stop" errorTitle="${xmlAttr("Valor fora da lista")}" error="${xmlAttr(l.erro)}"`
      : ` showErrorMessage="0"`;
    return `<dataValidation type="list" allowBlank="1" showInputMessage="1"${erro} sqref="${ref}"><formula1>${xmlText(formula)}</formula1></dataValidation>`;
  }).filter(Boolean);

  const bin = XLSX.write(wb, { type: "array", bookType: "xlsx", compression: true }) as ArrayBuffer;
  if (!validacoes.length) return new Uint8Array(bin);
  return injetarValidacoes(new Uint8Array(bin), wb.SheetNames.indexOf(abaDados) + 1, validacoes);
}

function xmlAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
function xmlText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// Elementos que, pela ordem do formato, vêm depois de <dataValidations>.
const DEPOIS_DE_VALIDACAO = [
  "<hyperlinks", "<printOptions", "<pageMargins", "<pageSetup", "<headerFooter", "<rowBreaks",
  "<colBreaks", "<customProperties", "<cellWatches", "<ignoredErrors", "<smartTags", "<drawing",
  "<legacyDrawing", "<legacyDrawingHF", "<picture", "<oleObjects", "<controls", "<webPublishItems",
  "<tableParts", "<extLst", "</worksheet>",
];

export function injetarValidacoes(xlsx: Uint8Array, numeroAba: number, validacoes: string[]): Uint8Array {
  const CFB = XL.CFB;
  const zip = CFB.read(xlsx, { type: "array" });
  const i = (zip.FullPaths as string[]).findIndex(p => p.endsWith(`xl/worksheets/sheet${numeroAba}.xml`));
  if (i < 0) return xlsx;
  const entrada = zip.FileIndex[i];
  let xml = new TextDecoder("utf-8").decode(entrada.content instanceof Uint8Array ? entrada.content : new Uint8Array(entrada.content));
  const bloco = `<dataValidations count="${validacoes.length}">${validacoes.join("")}</dataValidations>`;
  const pos = DEPOIS_DE_VALIDACAO.map(t => xml.indexOf(t)).filter(p => p >= 0).sort((a, b) => a - b)[0];
  if (pos == null) return xlsx;
  xml = xml.slice(0, pos) + bloco + xml.slice(pos);
  const novo = new TextEncoder().encode(xml);
  entrada.content = novo;
  entrada.size = novo.length;
  const out = CFB.write(zip, { fileType: "zip", type: "array", compression: true });
  return out instanceof Uint8Array ? out : new Uint8Array(out);
}

// Id de lote tirado do conteúdo (cliente, tipo, cabeçalhos, linhas, colunas
// ignoradas): a mesma planilha dá sempre o mesmo lote, então reescolher o
// arquivo, fechar e reabrir ou recarregar a página não grava duas vezes.
// Hash de 128 bits (cyrb128); não é criptográfico, e não precisa ser: o
// servidor ainda confere cliente e tipo do lote. crypto.subtle não existe na
// HML (http), por isso o hash é feito aqui.
function cyrb128(str: string): number[] {
  let h1 = 1779033703, h2 = 3144134277, h3 = 1013904242, h4 = 2773480762;
  for (let i = 0; i < str.length; i++) {
    const k = str.charCodeAt(i);
    h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
    h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
    h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
    h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
  }
  h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
  h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
  h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
  h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
  h1 ^= h2 ^ h3 ^ h4; h2 ^= h1; h3 ^= h1; h4 ^= h1;
  return [h1 >>> 0, h2 >>> 0, h3 >>> 0, h4 >>> 0];
}
function comoUuid(partes: number[]): string {
  const h = partes.map(x => x.toString(16).padStart(8, "0")).join("").split("");
  h[12] = "8";                                                  // versão 8 (formato próprio)
  h[16] = ((parseInt(h[16], 16) & 0x3) | 0x8).toString(16);     // variante RFC
  const s = h.join("");
  return `${s.slice(0, 8)}-${s.slice(8, 12)}-${s.slice(12, 16)}-${s.slice(16, 20)}-${s.slice(20)}`;
}
export function loteDoConteudo(clientId: number, entrada: EntradaLote): string {
  const ign = [...new Set(entrada.ignorar ?? [])].sort((a, b) => a - b);
  return comoUuid(cyrb128(JSON.stringify([clientId, entrada.tipo, entrada.cabecalhos, entrada.linhas.map(l => [l.n, l.c]), ign])));
}
// "Gravar de novo mesmo assim": lote novo, ligado à mesma planilha.
export function loteDeReimportacao(base: string): string {
  return comoUuid(cyrb128(`${base}:${novoLoteId()}`));
}

// Lote em uso para uma planilha, guardado na aba do navegador até a resposta
// definitiva do servidor (sobrevive a recarregar a página).
const CHAVE_PENDENTE = (base: string) => `sh_importacao_lote:${base}`;
export function lotePendente(base: string): string | null {
  try { return sessionStorage.getItem(CHAVE_PENDENTE(base)); } catch { return null; }
}
export function guardarLotePendente(base: string, lote: string) {
  try { sessionStorage.setItem(CHAVE_PENDENTE(base), lote); } catch { /* sem sessionStorage: vale o lote do conteúdo */ }
}
export function limparLotePendente(base: string) {
  try { sessionStorage.removeItem(CHAVE_PENDENTE(base)); } catch { /* idem */ }
}

// Lote para mandar ao gravar. Se há um lote pendente (enviado sem resposta
// definitiva) para esta planilha, ele é reaproveitado, também na reimportação;
// lote de reimportação novo só quando não há pendente.
export function loteParaGravar(base: string, reimportar: boolean): string {
  return lotePendente(base) ?? (reimportar ? loteDeReimportacao(base) : base);
}

// O que fazer depois de um erro ao gravar:
// - "incerto": sem resposta do servidor (ou erro do banco): pode ter gravado;
//   o lote fica o mesmo e a prévia é refeita (ela mostra se entrou);
// - "tentar_de_novo": nada foi gravado (trava demorou); mesmo lote;
// - "nada_gravado": CONFLICT ou BAD_REQUEST, certeza de que nada entrou; solta o lote.
export type DesfechoErro = "incerto" | "tentar_de_novo" | "nada_gravado";
export function desfechoDoErro(e: any): DesfechoErro {
  const code = e?.data?.code;
  if (!code) return "incerto";
  if (e?.data?.motivo === "tentar_de_novo") return "tentar_de_novo";
  if (code === "CONFLICT" || code === "BAD_REQUEST") return "nada_gravado";
  return "incerto";
}

// Mensagem de erro em português. Erro de formato da requisição (zod) vira
// texto simples; o resto já vem do servidor em português.
export function mensagemDeErro(e: any, padrao: string): string {
  const msg = String(e?.message ?? "");
  const ehZod = !!e?.data?.zodError || (e?.data?.code === "BAD_REQUEST" && /^\s*\[\s*\{/.test(msg) && /"(code|path|origin)"\s*:/.test(msg));
  if (ehZod) return "A planilha tem algum valor fora do formato que o Social Hub aceita (célula ou cabeçalho grande demais, ou linhas demais). Confira a planilha e tente de novo.";
  return msg || padrao;
}

// Id de lote (UUID v4). crypto.randomUUID só existe em página segura (https ou
// localhost); a HML é http, então cai para getRandomValues.
export function novoLoteId(): string {
  const c: any = (globalThis as any).crypto;
  if (typeof c?.randomUUID === "function") return c.randomUUID();
  const b: Uint8Array = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, x => x.toString(16).padStart(2, "0")).join("");
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export function baixarArquivo(bytes: Uint8Array, nome: string) {
  const blob = new Blob([bytes as BlobPart], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nome;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}
